import { useState } from "react";
import { Box, Button } from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import { ResetPasswordModal } from "../ResetPassword/ResetPasswordModal";

/**
 * FR-008's single page-header action slot: one Save, plus Reset Password.
 *
 * **Save renders inactive** (FR-052). Its write endpoint does not exist — the lambda exposes four read
 * handlers and its dispatch ignores HTTP method, so a POST is served as a read. `disabled` on the
 * component is what conveys "unavailable" to assistive technology; FR-052 forbids a control that
 * merely does nothing when clicked.
 *
 * **Save's `disabled` is hardcoded, and as of 2026-08-31 that is a decision rather than a wart.** The
 * User Information fields became editable that day, so the form *can* now be dirty and
 * `disabled={!isDirty}` — FR-008a's real rule — is finally implementable. It is deliberately not
 * implemented: with no write endpoint, a Save that lights up when the form is dirty is a control that
 * looks live and cannot act, which is exactly what FR-052 forbids. `useUserInfoEdits` exposes
 * `isDirty` ready for the day the endpoint lands; wiring it here is a one-line change then.
 *
 * ⚠️ **What this costs, and it is a real gap.** Save now reads as "you may not save" rather than
 * "there is nothing to save", and nothing on the page explains which. FR-024's leave-the-page
 * unsaved-changes prompt is also still deferred, so a user who types and then navigates away loses
 * their typing with no warning. Tab switches are safe — FR-009 is honoured, the edits live in `MyInfo`
 * and survive — but leaving the page is not. Both want addressing before this is put in front of
 * users; neither blocks the editable-fields change that was asked for.
 *
 * **Reset Password is enabled and opens the dialog soft-disabled** (2026-08-27), superseding the
 * earlier decision to leave it inactive and the dialog unreachable. The reason it changed: Save reads
 * as "nothing to save" even when dead, but a dead dialog-opener reads as "you are not allowed" — and
 * with the FR-018 bridge notice also gone, nothing on the page explained it at all. The dialog now
 * states why it cannot act and where the user can still reset their password, with its fields and
 * confirm action disabled so there is no false-success path. See `ResetPasswordDialog` for why the
 * fields are disabled and not merely unsubmittable.
 *
 * **No unsaved-changes guard and no navigation prompt.** FR-024/FR-027/FR-027a remain deferred. Dirty
 * tracking itself now exists (`useUserInfoEdits.isDirty`) because the fields are editable, but the
 * leave-the-page prompt does not — see the warning above for the consequence. FR-009 is explicit that
 * switching tabs must never prompt, and it does not.
 *
 * `onConfirm` is wired to a no-op rather than left unwired: the confirm action can never fire while
 * `isWriteAvailable` is false, and a handler that throws or navigates would be a landmine for whoever
 * enables the endpoint. It is the single line Story 3 replaces with the mutation.
 *
 * ---
 *
 * ## FR-018's bridge-user explanation is deferred to Story 3 — re-add it when Reset Password is enabled
 *
 * FR-018 requires that a bridge user find Reset Password unavailable **and be told why**. That
 * explanation used to render here as an `Alert` beside the buttons. It was removed on 2026-08-27, for
 * two reasons:
 *
 * 1. **It explained nothing yet.** Under FR-052 Reset Password is disabled for *every* user, so there
 *    was no bridge-specific state to distinguish — the notice appeared beside a control that was
 *    equally dead for everyone. FR-018's trigger is a bridge user seeing an otherwise-*available*
 *    action withheld from them, which cannot happen while it is withheld from all.
 * 2. **It broke this row.** A block `Alert` inside a right-aligned action cluster in the card's
 *    identity row forced the cluster to the alert's width and wrapped the buttons.
 *
 * So FR-018 is not violated by the shipped build — its precondition is unreachable. It becomes
 * reachable the moment Story 3 enables this button, and the explanation MUST come back in the same
 * change. `MyInfoSubject.isBridgeUser` is still resolved and still covered by
 * `useMyInfoSubject.spec.ts`, so the data is ready; this component simply stops taking the subject.
 *
 * Two notes for whoever restores it:
 * - **Do not put it back inside this flex row.** Render it below the identity row, or in the User
 *   Information panel where the prototype puts it (`bridgeNote`) — not in the action cluster.
 * - Interstate's `ButtonProps` exposes no `aria-describedby` (only `aria-label`, which would replace
 *   the button's name with a sentence), so the reason cannot be associated with the control directly.
 *   The previous approach — an `Alert` with `role="status"`, putting the reason in the accessibility
 *   tree — is still the workable one unless the wrapper has since gained `aria-describedby`.
 */
export const HeaderActions = () => {
  const [isResetOpen, setIsResetOpen] = useState(false);

  return (
    <Box
      data-testid={"myinfo-header-actions-content"}
      sx={{
        alignItems: "center",
        columnGap: "8px",
        display: "flex",
        flexWrap: "wrap",
        rowGap: "8px"
      }}
    >
      {/*
       * Reset Password first, Save last — the approved prototype's order, and the conventional one:
       * the primary action sits at the end of the cluster, nearest the edge the eye finishes on. The
       * prototype's own header calls this order non-authoritative, so this is fidelity to the reviewed
       * mock rather than a requirement; it is safe to reorder if the team prefers otherwise.
       */}
      <Button
        analyticsConfiguration={{
          eventName: "myinfo-reset-password",
          eventProperties: undefined
        }}
        buttonStyle={"tertiary"}
        data-testid={"myinfo-reset-password"}
        onClick={() => setIsResetOpen(true)}
      >
        {Locale.getText("RESET_PASSWORD") as string}
      </Button>

      <Button
        disabled
        analyticsConfiguration={{
          eventName: "myinfo-save",
          eventProperties: undefined
        }}
        buttonStyle={"primary"}
        data-testid={"myinfo-save"}
      >
        {Locale.getText("SAVE") as string}
      </Button>

      <ResetPasswordModal
        isOpen={isResetOpen}
        isWriteAvailable={false}
        onClose={() => setIsResetOpen(false)}
        onConfirm={() => undefined}
      />
    </Box>
  );
};
